import {
  toWSDateString,
  WSAvatarIconProps,
  WSCard,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { ReactNode, useCallback, useMemo } from "react";
import { Route, useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { Summary } from "../../../Invoices/components/Summary/Summary";
import { PayableDetails } from "../../../Invoices/screens/payables/PayableDetails";
import { getNextPayrollCutOffDate } from "../../../Invoices/utils/payables";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayablesSummary } from "../../../query/payables/useQueryPayablesSummary";
import { useQueryPayablesWithPayees } from "../../../query/payables/useQueryPayablesWithPayees";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { Payable } from "../../../services/api/payments/payables";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { PATH_SETTINGS_PAYROLL_SCHEDULE } from "../../Settings/paths";
import { getTabFilter } from "../components/TablePayables/filters/request";
import { TablePayables } from "../components/TablePayables/TablePayables";
import {
  PATH_PAYABLES_DISPUTED,
  routeHelpersPayablesDetails,
  routeHelpersPayablesTab
} from "../paths";

const PAID_LAST_30_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const RECENT_ACTIVITY_LIMIT = 5;

type InfoCardProps = {
  avatar: Pick<WSAvatarIconProps, "icon" | "color" | "colorBackground">;
  headerText: string;
  actionLabel: string;
  onAction: () => void;
  children: ReactNode;
};

const InfoCard: React.FC<InfoCardProps> = props => (
  <WSCard
    header={{
      label: {
        avatar: { type: "icon", size: "M", ...props.avatar },
        text: props.headerText
      }
    }}
    divider="bottom"
    actions={{
      alignment: "left",
      buttons: [
        {
          label: props.actionLabel,
          kind: "Link",
          rightIcon: "chevron-right",
          onClick: props.onAction
        }
      ]
    }}
  >
    <WSText.ParagraphSm color="gray500">{props.children}</WSText.ParagraphSm>
  </WSCard>
);

const pluralPayable = (count: number) => `payable${count > 1 ? "s" : ""}`;

export const RoutePayablesDashboard: React.FC = () => {
  useBrowserPageTitle("Payables Dashboard");
  const userId = useUserId();
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();

  const paidSince = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return new Date(d.getTime() - PAID_LAST_30_DAYS_MS);
  }, []);

  const queryPayrollSettings = useQueryPayrollSettings(userId, {
    retry: false
  });
  const isDualStage =
    queryPayrollSettings.data?.workflow === PayrollWorkflowStrategy.DualStage;

  const queryOpenSummary = useQueryPayablesSummary({
    "filter[status][in]": ["Open", "Pending"]
  });
  const queryOverdueSummary = useQueryPayablesSummary({
    "filter[status][in]": ["Overdue"]
  });
  const queryPaidLast30DaysSummary = useQueryPayablesSummary({
    ...getTabFilter("paid"),
    "filter[events.paidAt][>]": paidSince
  });

  const queryScheduledSummary = useQueryPayablesSummary({
    ...getTabFilter("scheduled")
  });
  const queryToApproveSummary = useQueryPayablesSummary({
    ...getTabFilter("to-approve", isDualStage)
  });
  const queryDisputedSummary = useQueryPayablesSummary({
    "filter[payee.workflowStatus][=]": "Disputed"
  });

  const queryRecentPayables = useQueryPayablesWithPayees({
    "sort[updatedAt]": "desc"
  });

  const formatTotal = useCallback(
    (totalValue: number | undefined) =>
      totalValue !== undefined ? formatMoney(totalValue, defaultCurrency) : "–",
    [defaultCurrency]
  );

  const goToPayableDetails = routeHelpersPayablesDetails.useGoTo();
  const goToPayablesTab = routeHelpersPayablesTab.useGoTo();

  const handleRowClick = useCallback(
    (payable: Payable) => {
      if (payable.payableId) {
        goToPayableDetails({
          tab: "dashboard",
          payableId: payable.payableId
        });
      }
    },
    [goToPayableDetails]
  );

  const goToToApprove = () => goToPayablesTab({ tab: "to-approve" });
  const goToDisputed = () => history.push(PATH_PAYABLES_DISPUTED);

  return (
    <>
      <Route
        path={routeHelpersPayablesDetails.path}
        component={PayableDetails}
      />
      <WSList gap="2XL">
        <WSElement>
          <WSGrid>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Open"
                color="amber300"
                value={formatTotal(queryOpenSummary.data?.totalValue)}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Overdue"
                color="red300"
                value={formatTotal(queryOverdueSummary.data?.totalValue)}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "8", s: "4", m: "3" }}>
              <Summary
                label="Paid (last 30 days)"
                color="green300"
                value={formatTotal(queryPaidLast30DaysSummary.data?.totalValue)}
              />
            </WSGrid.Item>
          </WSGrid>
        </WSElement>

        <WSList gap="L">
          {!!queryScheduledSummary.data?.listSize &&
            !!queryPayrollSettings.data && (
              <InfoCard
                avatar={{
                  icon: "calendar",
                  color: "blue500",
                  colorBackground: "blue50"
                }}
                headerText={`Next payroll: ${toWSDateString(getNextPayrollCutOffDate(queryPayrollSettings.data), "monthDate")}`}
                actionLabel="Manage payroll settings"
                onAction={() => {
                  history.push(PATH_SETTINGS_PAYROLL_SCHEDULE);
                }}
              >
                {queryScheduledSummary.data?.listSize}{" "}
                {pluralPayable(queryScheduledSummary.data?.listSize)} totaling{" "}
                {formatMoney(
                  queryScheduledSummary.data?.totalValue ?? 0,
                  defaultCurrency
                )}
              </InfoCard>
            )}

          {!!queryToApproveSummary.data?.listSize && (
            <InfoCard
              avatar={{
                icon: "alert",
                color: "amber400",
                colorBackground: "amber50"
              }}
              headerText="To approve:"
              actionLabel="Review and approve"
              onAction={goToToApprove}
            >
              {queryToApproveSummary.data.listSize}{" "}
              {pluralPayable(queryToApproveSummary.data.listSize)} totaling{" "}
              {formatMoney(
                queryToApproveSummary.data.totalValue ?? 0,
                defaultCurrency
              )}
            </InfoCard>
          )}

          {!!queryDisputedSummary.data?.listSize && (
            <InfoCard
              avatar={{
                icon: "alert-circle",
                color: "amber400",
                colorBackground: "amber50"
              }}
              headerText="Disputed payables"
              actionLabel="Manage disputed payables"
              onAction={goToDisputed}
            >
              {queryDisputedSummary.data.listSize} disputed{" "}
              {pluralPayable(queryDisputedSummary.data.listSize)} that need your
              attention.
            </InfoCard>
          )}
        </WSList>

        <WSElement>
          <WSText.Heading5 mb="M">Recent payable activity</WSText.Heading5>
          <WSQueries
            queries={{ queryRecentPayables }}
            renderErrors={() => null}
          >
            {({ queryRecentPayables: { data: recentPayables } }) => {
              const visibleRecent = recentPayables.slice(
                0,
                RECENT_ACTIVITY_LIMIT
              );
              if (visibleRecent.length === 0) {
                return (
                  <WSEmptyState
                    py="3XL"
                    hasBorder
                    bgColor="gray"
                    type="inbox"
                    title="No recent activity"
                    description="New and recently changed payables will display here."
                  />
                );
              }
              return (
                <TablePayables
                  payables={visibleRecent}
                  onRowClick={handleRowClick}
                  payeesMap={queryRecentPayables.payeesMap}
                  visibleColumns={{
                    payee: true,
                    payableNumber: true,
                    amount: true,
                    dueDate: false,
                    status: true
                  }}
                />
              );
            }}
          </WSQueries>
        </WSElement>
      </WSList>
    </>
  );
};
